import React, {RefObject} from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {connect} from "react-redux";
import {bindActionCreators, Dispatch} from "redux";
import {
    BulkProducts,
    GetBulkProductSearchDataResponse,
    ProductInfo,
    ProgramInput,
} from "src/model/ProductMatch";
import MetricsUtil from "src/utils/MetricsUtil";
import ApplicationState from "src/model/ApplicationState";
import {
    KatBox,
    KatButton,
    KatCheckbox,
    KatDropdown,
    KatInputGroup,
    KatLabel,
    KatOption,
    KatTable,
    KatTableBody,
    KatTableCell,
    KatTableHead,
    KatTableRow
} from "@amzn/katal-react";
import {getCurrencySymbolConfig, PROGRAM_NAME_OF_MFN} from "src/config/Config";
import NumberKatInput from "src/components/common/NumberKatInput";
import {setBulkProductsQueryStart} from "src/actions/BulkReportQueryAction";
import {updateProduct} from "src/actions/BulkProductAction";

interface StateProps {
    bulkProductsRes: BulkProducts,
    bulkProductSearchRes: GetBulkProductSearchDataResponse,
}

interface OwnProps {
    readonly currentCountryCode: string,
    readonly metricsUtil?: MetricsUtil,
    readonly program: string,
    handleNext: Function
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    bulkProductsRes: state.bulkProductsRes,
    bulkProductSearchRes: state.bulkProductSearchRes,
});

interface DispatchProps {
    readonly dispatchBulkProductsQueryStart: (countryCode: string, products: Array<ProductInfo>) => void,
    readonly dispatchUpdateProduct: (product: ProductInfo, oldProduct: ProductInfo) => void,
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchBulkProductsQueryStart: setBulkProductsQueryStart,
        dispatchUpdateProduct: updateProduct,
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
}

export class BulkProductProgramTableComponent extends React.Component<Props, State> {

    protected readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);

        this.state = {};

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }

        this.handleNumChange = this.handleNumChange.bind(this);
        this.handlePriceChange = this.handlePriceChange.bind(this);
        this.handleServiceChange = this.handleServiceChange.bind(this);
    }

    getCurrencySymbol(): string {
        return getCurrencySymbolConfig(this.props.currentCountryCode);
    };

    generateServiceAddOnContent(key: string, p: ProductInfo) {
        const {t} = this.props;
        return (
            <KatDropdown value="abc" className="dropdown-service-add-on" key={key + "service-add-on"} placeholder="service selected">
                <KatOption>
                    <KatCheckbox label={t("SC_FBA_Calc_label_placement")} checked={p.programInputMap?.get(this.props.program)?.isPlacement}
                                 onChange={e => {
                                     this.handleServiceChange(e.detail.checked, p, "isPlacement")
                                 }}/>
                </KatOption>
                <KatOption>
                    <KatCheckbox label={t("SC_FBA_Calc_label_opaueBagging")}
                                 checked={p.programInputMap?.get(this.props.program)?.isOpaqueBagging} onChange={e => {
                        this.handleServiceChange(e.detail.checked, p, "isOpaqueBagging")
                    }}/>
                </KatOption>
                <KatOption>
                    <KatCheckbox label={t("SC_FBA_Calc_label_tape")} checked={p.programInputMap?.get(this.props.program)?.isTape}
                                 onChange={e => {
                                     this.handleServiceChange(e.detail.checked, p, "isTape")
                                 }}/>
                </KatOption>
                <KatOption>
                    <KatCheckbox label={t("SC_FBA_Calc_label_label")} checked={p.programInputMap?.get(this.props.program)?.isLabel}
                                 onChange={e => {
                                     this.handleServiceChange(e.detail.checked, p, "isLabel")
                                 }}/>
                </KatOption>
                <KatOption>
                    <KatCheckbox label={t("SC_FBA_Calc_label_bagging")} checked={p.programInputMap?.get(this.props.program)?.isBagging}
                                 onChange={e => {
                                     this.handleServiceChange(e.detail.checked, p, "isBagging")
                                 }}/>
                </KatOption>
                <KatOption>
                    <KatCheckbox label={t("SC_FBA_Calc_label_bubbleWrap")}
                                 checked={p.programInputMap?.get(this.props.program)?.isBubbleWrap} onChange={e => {
                        this.handleServiceChange(e.detail.checked, p, "isBubbleWrap")
                    }}/>
                </KatOption>
            </KatDropdown>
        );
    }

    generateInputCurrencyBlockContent(defaultValue: number, handleInput: Function, product: ProductInfo, paramName: string, ref?: RefObject<any>, nextRef?: RefObject<any>) {
        return (
            <div className="input-block-group-box">
                <KatInputGroup className="input-block-inputgroup">
                    <KatLabel text={this.getCurrencySymbol()} className="input-block-symbol"/>
                    <NumberKatInput isDisabled={false} className="input-block-input-price" minValue={0} isFloat={true}
                                    value={defaultValue != 0 ? defaultValue : undefined} ref={ref} nextRef={nextRef}
                                    triggerInputEventForDefaultValue={false}
                                    onInput={(input: number, hasError: boolean): void => {
                                        if (!hasError) {
                                            handleInput(input, product, paramName);
                                        }
                                    }}/>
                </KatInputGroup>
            </div>
        );
    }

    generateInputNumberBlockContent(defaultValue: number, handleInput: Function, product: ProductInfo, paramName: string, ref?: RefObject<any>, nextRef?: RefObject<any>) {
        return (
            <NumberKatInput isDisabled={false} className="input-block-inputbox" minValue={0} isFloat={false} isMandatory={true}
                            value={defaultValue != 0 ? defaultValue : undefined} ref={ref} nextRef={nextRef}
                            triggerInputEventForDefaultValue={false}
                            onInput={(input: number, hasError: boolean): void => {
                                if (!hasError) {
                                    handleInput(input, product, paramName);
                                }
                            }}/>
        );
    }

    isMfn(program: string) {
        return program == PROGRAM_NAME_OF_MFN;
    }

    productTableRow(product: ProductInfo, program: string, index: number) {
        const key = product.asin + product.fnsku + product.merchantSku;
        const currency = this.getCurrencySymbol();
        const isMfn = this.isMfn(program);
        return (
            <>
                <KatTableRow key={key + "row"} className={"bulk-table-row-" + (index % 2)}>
                    <KatTableCell key={key + "cell-1"}><img src={product.imageUrl} className="bulk-product-img"/></KatTableCell>
                    <KatTableCell key={key + "cell-2"}>{product.title}</KatTableCell>
                    <KatTableCell key={key + "cell-3"}>
                        {this.generateInputCurrencyBlockContent(product.price ? product.price : 1, this.handlePriceChange, product, "price")}
                    </KatTableCell>
                    {!isMfn && <KatTableCell key={key + "cell-4"}>
                        {this.generateServiceAddOnContent(key, product)}
                    </KatTableCell>}
                    <KatTableCell key={key + "cell-5"}>
                        {this.generateInputNumberBlockContent(product.programInputMap?.get(this.props.program)?.averageInventoryUnitsStored ?? 1, this.handleNumChange, product, "averageInventoryUnitsStored")}
                    </KatTableCell>
                    <KatTableCell key={key + "cell-6"}>
                        {this.generateInputNumberBlockContent(product.programInputMap?.get(this.props.program)?.estimatedMonthlyUnitsSold ?? 1, this.handleNumChange, product, "estimatedMonthlyUnitsSold")}
                    </KatTableCell>
                    <KatTableCell key={key + "cell-7"}>
                        {this.generateInputCurrencyBlockContent(product.programInputMap?.get(this.props.program)?.costOfGoodsSold ?? 0, this.handleNumChange, product, "costOfGoodsSold")}
                    </KatTableCell>
                    <KatTableCell key={key + "cell-8"}>
                        {this.generateInputCurrencyBlockContent(product.programInputMap?.get(this.props.program)?.miscellaneousCost ?? 0, this.handleNumChange, product, "miscellaneousCost")}
                    </KatTableCell>
                    <KatTableCell key={key + "cell-9"}>
                        {this.generateInputNumberBlockContent(product.programInputMap?.get(this.props.program)?.estimatedSales ?? 1, this.handleNumChange, product, "estimatedSales")}
                    </KatTableCell>
                    {!isMfn && <KatTableCell key={key + "cell-10"}>
                        {this.generateInputCurrencyBlockContent(product.programInputMap?.get(this.props.program)?.sendToAmazon ?? 1, this.handleNumChange, product, "sendToAmazon")}
                    </KatTableCell>}
                    {isMfn && <KatTableCell key={key + "cell-11"}>
                        {this.generateInputCurrencyBlockContent(product.programInputMap?.get(this.props.program)?.shippingCharge ?? 1, this.handleNumChange, product, "shippingCharge")}
                    </KatTableCell>}
                    {isMfn && <KatTableCell key={key + "cell-12"}>
                        {this.generateInputCurrencyBlockContent(product.programInputMap?.get(this.props.program)?.fulfillCost ?? 1, this.handleNumChange, product, "fulfillCost")}
                    </KatTableCell>}
                    {isMfn && <KatTableCell key={key + "cell-13"}>
                        {this.generateInputCurrencyBlockContent(product.programInputMap?.get(this.props.program)?.monthlyStorageCostPerUnit ?? 1, this.handleNumChange, product, "monthlyStorageCostPerUnit")}
                    </KatTableCell>}
                </KatTableRow>
            </>
        )
    }

    programTableHeader(program: string) {
        const isMfn = this.isMfn(program);
        const {t} = this.props;
        return (
            <>
                <KatTableHead>
                    <KatTableRow>
                        <KatTableCell>{t("SC_FBA_Calc_bulk_image")}</KatTableCell>
                        <KatTableCell>{t("SC_FBA_Calc_bulk_product_name")}</KatTableCell>
                        <KatTableCell>{t("SC_FBA_Calc_bulk_price")}</KatTableCell>
                        {/*FBA ONLY*/}
                        {!isMfn && <KatTableCell>{t("SC_FBA_Calc_label_serviceAddons")}</KatTableCell>}
                        <KatTableCell>{t("SC_FBA_Calc_AIUS")}</KatTableCell>
                        <KatTableCell>{t("SC_FBA_Calc_label_estimatedMontlyUnitsSold")}</KatTableCell>
                        <KatTableCell>{t("SC_FBA_Calc_label_costOfGoodsSold")}</KatTableCell>
                        <KatTableCell>{t("SC_FBA_Calc_label_miscellaneousCost")}</KatTableCell>
                        <KatTableCell>{t("SC_FBA_Calc_label_estimatedSales")}</KatTableCell>
                        {/*FBA ONLY*/}
                        {!isMfn && <KatTableCell>{t("SC_FBA_Calc_label_sendToAmazon")}</KatTableCell>}
                        {/*MFN ONLY*/}
                        {isMfn && <KatTableCell>{t("SC_FBA_Calc_label_shippingCharge")}</KatTableCell>}
                        {isMfn && <KatTableCell>{t("SC_FBA_Calc_fulfillCost")}</KatTableCell>}
                        {isMfn && <KatTableCell>{t("SC_FBA_Calc_MSCPU")}</KatTableCell>}
                    </KatTableRow>
                </KatTableHead>
            </>
        )
    }

    handleServiceChange(value: boolean, p: ProductInfo, paramName: string) {
        const product = {...p};
        const programInput: ProgramInput = {
            ...p.programInputMap?.get(this.props.program),
            [paramName]: value
        }
        product.programInputMap?.set(this.props.program, programInput);
        this.publishCounter(paramName, "select")
        this.props.dispatchUpdateProduct(product, p);
    }

    handleNumChange(number: number, p: ProductInfo, paramName: string) {
        const product = {...p};
        const programInput: ProgramInput = {
            ...p.programInputMap?.get(this.props.program),
            [paramName]: number
        }
        product.programInputMap?.set(this.props.program, programInput);
        this.publishCounter(paramName, "input")
        this.props.dispatchUpdateProduct(product, p);
    }

    handlePriceChange(number: number, p: ProductInfo, paramName: string) {
        const product = {...p, price: number};
        this.props.dispatchUpdateProduct(product, p);
    }

    publishCounter(method: string, counter: string) {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(method, counter);
        }
    }

    render() {
        const {t} = this.props;
        const selectedProducts = this.props.bulkProductsRes.products;

        return (
            <>
                <KatTable key="program-input-table">
                    {this.programTableHeader(this.props.program)}
                    <KatTableBody>
                        {
                            selectedProducts.map((product, index) =>
                                (this.productTableRow(product, this.props.program, index)))
                        }
                    </KatTableBody>
                </KatTable>
                <KatBox variant="ltgrey" className="bulk-search-product-table-program-box">
                    <KatButton label={t("SC_FBA_Calc_bulk_next")} variant="primary" className="bulk-search-step-next" onClick={() => {
                        if (this.props.bulkProductsRes.programs.lastIndexOf(this.props.program) == (this.props.bulkProductsRes.programs.length - 1)) {
                            this.props.dispatchBulkProductsQueryStart(this.props.currentCountryCode, selectedProducts);
                        }
                        this.props.handleNext()
                    }}/>
                </KatBox>
            </>
        )
    }
}

export default withTranslation()(connector(BulkProductProgramTableComponent));
